/* React */
import { Component } from "react"
/* ReactDOM portal */
import ReactDOM from "react-dom"
/* Redux connect */
import { connect } from "react-redux"

/* Icon component */
import Icon from "./Icon"
/* Notification API */
import { list, read, readAll } from "../api/Notification"
/* Translation */
import { translate } from "../translates"

const TONES = {
    success: { bg: "var(--success-bg)", color: "var(--success)", icon: "check" },
    accent: { bg: "var(--accent-50)", color: "var(--accent)", icon: "star" },
    warn: { bg: "#fef3c7", color: "var(--warn)", icon: "clock" },
    danger: { bg: "var(--danger-bg)", color: "var(--danger)", icon: "heart" },
    info: { bg: "var(--accent-50)", color: "var(--accent)", icon: "bell" }
}

/* Component NotificationsDrawer */
class NotificationsDrawer extends Component {

    constructor(props) {
        super(props)
        /* State */
        this.state = { loading: true, error: false, items: [] }
    }

    componentDidMount = () => {
        document.addEventListener("keydown", this._onKey)
        document.body.style.overflow = "hidden"
        if (this.props.token) {
            this._load()
        }
        else {
            this.setState({ loading: false })
        }
    }

    componentWillUnmount = () => {
        document.removeEventListener("keydown", this._onKey)
        document.body.style.overflow = ""
    }

    /* Draw On Key */
    _onKey = (event) => {
        if (event.key === "Escape") {
            this.props.onClose()
        }
    }

    /* Draw Load */
    _load = () => {
        this.setState({ loading: true, error: false })
        list(this.props.token).then((response) => {
            if (response && response.status === 200) {
                this.setState({ items: Array.isArray(response.data) ? response.data : [], loading: false })
            }
            else {
                this.setState({ loading: false, error: true })
            }
        })
    }

    /* Draw Read All */
    _readAll = () => {
        readAll(this.props.token).then(() => {
            this.setState({ items: this.state.items.map((item) => ({ ...item, read: true })) })
        })
    }

    /* Draw Read */
    _read = (id) => {
        read(this.props.token, id)
        this.setState({ items: this.state.items.map((item) => (item.id === id ? { ...item, read: true } : item)) })
    }

    /* Draw Is Today */
    _isToday = (dateString) => {
        if (!dateString) {
            return false
        }
        const date = new Date(dateString)
        const today = new Date()
        return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
    }

    /* Draw Format Time */
    _formatTime = (dateString) => {
        if (!dateString) {
            return ""
        }
        const date = new Date(dateString)
        if (isNaN(date.getTime())) {
            return dateString
        }
        const now = new Date()
        const diffMs = now - date
        const diffMin = Math.floor(diffMs / 60000)
        const diffHours = Math.floor(diffMs / 3600000)
        const diffDays = Math.floor(diffMs / 86400000)

        if (diffMin < 1) {
            return translate("только что")
        }
        if (diffMin < 60) {
            return `${diffMin} ${translate("мин назад")}`
        }
        if (diffHours < 24) {
            return `${diffHours} ${translate("ч назад")}`
        }
        if (diffDays === 1) {
            return translate("вчера")
        }
        if (diffDays < 7) {
            const lastDigit = diffDays % 10
            if (lastDigit === 1) {
                return `${diffDays} ${translate("день назад")}`
            }
            if (lastDigit >= 2 && lastDigit <= 4) {
                return `${diffDays} ${translate("дня назад")}`
            }
            return `${diffDays} ${translate("дней назад")}`
        }
        const months = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]
        return `${date.getDate()} ${translate(months[date.getMonth()])}`
    }

    /* Draw Groups */
    _groups = () => {
        /* Fields */
        const { items } = this.state
        const today = []
        const earlier = []
        items.forEach((item) => {
            if (this._isToday(item.createdAt)) {
                today.push(item)
            }
            else {
                earlier.push(item)
            }
        })
        const groups = []
        if (today.length) {
            groups.push({ label: "Сегодня", items: today })
        }
        if (earlier.length) {
            groups.push({ label: "Ранее", items: earlier })
        }
        if (!groups.length && items.length) {
            groups.push({ label: "Уведомления", items })
        }
        return groups
    }

    /* Draw Notif Item */
    _notifItem = (notification) => {
        const tone = TONES[notification.tone] || TONES.info
        const unread = !notification.read

        return (
            <div
                key={`${notification.id}`}
                className={`notif${unread ? " is-unread" : ""}`}
                onClick={() => { if (unread) this._read(notification.id) }}
            >
                <div className="notif__ic" style={{ background: tone.bg, color: tone.color }}>
                    <Icon name={notification.icon || tone.icon} size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="notif__title">{notification.title}</div>
                    <div className="notif__text">{notification.text}</div>
                </div>
                <div className="notif__meta">
                    <span className="notif__time">{notification.time || this._formatTime(notification.createdAt)}</span>
                    {unread && <span className="notif__dot" />}
                </div>
            </div>
        )
    }

    /* Draw Body */
    _body = () => {
        /* Fields */
        const { loading, error, items } = this.state

        if (loading) {
            return (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 200 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid var(--line)", borderTopColor: "var(--accent)", animation: "bkspin 0.7s linear infinite" }} />
                </div>
            )
        }

        if (!this.props.token) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
                    <div className="notif-empty__ic"><Icon name="bell" size={30} /></div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "var(--ink)", marginBottom: 8 }}>{translate("Войдите, чтобы видеть уведомления")}</div>
                    <div style={{ fontSize: 14, marginBottom: 20 }}>{translate("Здесь будут записи, напоминания и предложения")}</div>
                    <button className="btn" style={{ padding: "10px 24px" }} onClick={() => { this.props.onClose(); this.props.toggleAuth(true) }}>{translate("Войти")}</button>
                </div>
            )
        }

        if (error) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <div style={{ color: "var(--danger)", marginBottom: 12 }}><Icon name="bell" size={32} /></div>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>{translate("Не удалось загрузить")}</div>
                    <button className="btn btn--ghost" style={{ padding: "8px 16px" }} onClick={this._load}>{translate("Повторить")}</button>
                </div>
            )
        }

        if (!items.length) {
            return (
                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted)" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: "var(--ink)", marginBottom: 8 }}>{translate("Уведомлений пока нет")}</div>
                    <div style={{ fontSize: 14 }}>{translate("Здесь появятся записи, напоминания и предложения")}</div>
                </div>
            )
        }

        const groups = this._groups()

        return (
            <>
                {groups.map((group) => (
                    <div key={group.label} style={{ marginBottom: 20 }}>
                        <div className="rdrawer__group-label">{translate(group.label)}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {group.items.map((notification) => this._notifItem(notification))}
                        </div>
                    </div>
                ))}
                <button
                    className="notif-settings"
                    onClick={() => { this.props.onClose(); this.props.navigate("/notifications") }}
                >
                    <div className="notif__ic" style={{ background: "var(--accent-50)", color: "var(--accent)" }}>
                        <Icon name="settings" size={18} />
                    </div>
                    <div style={{ flex: 1, textAlign: "left" }}>
                        <div className="notif__title">{translate("Настройки уведомлений")}</div>
                        <div className="notif__text">{translate("Push, SMS и email — выберите, что получать")}</div>
                    </div>
                    <Icon name="chevron_right" size={18} style={{ color: "var(--muted-2)" }} />
                </button>
            </>
        )
    }

    render = () => {
        /* Fields */
        const { onClose } = this.props

        return ReactDOM.createPortal(
            <div className="rdrawer" onClick={onClose}>
                <div className="rdrawer__panel" onClick={(event) => event.stopPropagation()}>
                    <div className="rdrawer__header">
                        <div>
                            <h3 className="rdrawer__title">{translate("Уведомления")}</h3>
                            <p className="rdrawer__sub">{translate("Записи, напоминания и предложения")}</p>
                        </div>
                        <button className="modal__close" onClick={onClose} aria-label={translate("Закрыть")}>
                            <Icon name="close" size={20} />
                        </button>
                    </div>
                    <div className="rdrawer__body">
                        {this._body()}
                    </div>
                </div>
            </div>,
            document.body
        )
    }
}

/* Map State To Props */
const mapStateToProps = state => {
    return {
        token: state.token,
        lang: state.lang
    }
}

/* Map Dispatch To Props */
const mapDispatchToProps = dispatch => {
    return {
        toggleAuth: data => dispatch({ type: "TOGGLE_AUTH", payload: data })
    }
}

/* Export */
export default connect(mapStateToProps, mapDispatchToProps)(NotificationsDrawer)
